/**
 * 로그인 전 화면 언어의 브라우저 기억.
 *
 * 로그인 전 화면(로그인·회원가입·비밀번호 찾기 등)은 사용자를 모르므로 계정 언어(users.locale)를 쓸 수 없음.
 * - lang: 이 브라우저에서 마지막으로 쓴 언어 = 로그인 전 화면의 처음 언어. 로그인하면 계정 언어로 덮어씀
 * - picked: 로그인 전 화면에서 직접 골랐고 아직 계정에 저장하지 않음 → 다음 로그인 때 계정 언어로 저장
 *
 * 🔴 로그인 사용자의 언어 SSOT 는 계속 DB(users.locale)임. 이 값은 로그인 전 화면 표시와 로그인 시 저장 요청에만 씀.
 * 저장소를 쓸 수 없는 브라우저(사생활 모드 등)는 기억 없이 동작함.
 *
 * 값은 검증하지 않은 문자열로 돌려줌 — 호출부가 isSupportedLng 로 확인함(i18n 초기화와의 순환 import 방지).
 */
const STORAGE_KEY = "eqms:ui-lang";

interface StoredLang {
  lang: string;
  picked: boolean;
}

function read(): StoredLang | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const { lang, picked } = value as Record<string, unknown>;
    return typeof lang === "string" ? { lang, picked: picked === true } : null;
  } catch {
    return null; // 깨진 값·저장소 차단 = 기억 없음
  }
}

function write(value: StoredLang): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // 저장소 차단 — 기억 없이 동작(화면 언어 전환은 이미 적용됨)
  }
}

/** 로그인 전 화면의 처음 언어(없으면 null). */
export function readBrowserLang(): string | null {
  return read()?.lang ?? null;
}

/** 로그인 전 화면에서 직접 고른 언어 — 다음 로그인 때 계정 언어로 저장됨. */
export function pickBrowserLang(lang: string): void {
  write({ lang, picked: true });
}

/** 계정 언어를 적용한 뒤 기억 — 로그아웃 후 로그인 화면이 그 언어로 열림. */
export function rememberBrowserLang(lang: string): void {
  write({ lang, picked: false });
}

/** 아직 계정에 저장하지 않은 직접 고른 언어를 꺼냄(한 번만). 없으면 null. */
export function takePickedLang(): string | null {
  const stored = read();
  if (!stored?.picked) return null;
  write({ lang: stored.lang, picked: false });
  return stored.lang;
}
